import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import BrandMark from "@/components/app/BrandMark";

function BrandPanel() {
  return (
    <div
      className="hidden lg:flex relative bg-slate-900"
      style={{
        backgroundImage:
          "url(https://images.unsplash.com/photo-1758876734777-dcc6981f3671?w=1400&q=80)",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute inset-0 bg-slate-900/70" />
      <div className="relative z-10 p-12 flex flex-col justify-between text-white w-full">
        <BrandMark size="lg" tone="light" />

        <div>
          <h1 className="font-display text-4xl xl:text-5xl font-black tracking-tighter mb-4">
            Hand-Arm Vibration
            <br />
            Exposure Control
          </h1>
          <p className="text-slate-300 text-base max-w-md leading-relaxed">
            Real-time trigger-time logging, HSE points calculation, and
            compliance reporting for industrial worksites.
          </p>
          <div className="mt-8 flex items-center gap-2 text-sm text-slate-300">
            <ShieldCheck className="h-4 w-4 text-emerald-400" />
            UK HSE compliant · Action 100 pts · Limit 400 pts
          </div>
        </div>
      </div>
    </div>
  );
}

function DemoAccountList() {
  return (
    <div className="mt-8 pt-6 border-t border-slate-200 text-xs text-slate-500 space-y-1">
      <p className="font-bold uppercase tracking-wider text-slate-700">
        Demo accounts
      </p>
      <p>
        <span className="font-mono">manager@havs.local</span> ·{" "}
        <span className="font-mono">Manager@123</span>
      </p>
      <p>
        <span className="font-mono">worker@havs.local</span> ·{" "}
        <span className="font-mono">Worker@123</span>
      </p>
    </div>
  );
}

function LoginForm({ onSuccess }) {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Signed in as ${u.name}`);
      onSuccess(u);
    } catch (err) {
      console.error("Login failed:", err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Sign-in failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <Label
          htmlFor="email"
          className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500"
        >
          Email
        </Label>
        <Input
          id="email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="h-11 mt-2"
          placeholder="manager@havs.local"
          required
          data-testid="login-email-input"
        />
      </div>
      <div>
        <Label
          htmlFor="password"
          className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500"
        >
          Password
        </Label>
        <Input
          id="password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="h-11 mt-2"
          required
          data-testid="login-password-input"
        />
      </div>
      <Button
        type="submit"
        disabled={busy}
        className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-medium"
        data-testid="login-submit-btn"
      >
        {busy ? "Signing in…" : "Sign in"}
      </Button>
    </form>
  );
}

export default function Login() {
  const { user } = useAuth();
  const navigate = useNavigate();

  if (user) {
    return <Navigate to={user.role === "manager" ? "/dashboard" : "/worker"} replace />;
  }

  const onSuccess = (u) =>
    navigate(u.role === "manager" ? "/dashboard" : "/worker", { replace: true });

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <BrandPanel />
      <div className="flex items-center justify-center p-6 sm:p-12">
        <Card
          className="w-full max-w-md p-8 border border-slate-200 shadow-sm"
          data-testid="login-card"
        >
          <div className="flex items-center gap-2 mb-2 lg:hidden">
            <BrandMark size="md" tone="dark" />
          </div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">
            Sign in
          </p>
          <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900 mb-1">
            Welcome back
          </h2>
          <p className="text-sm text-slate-600 mb-8">
            Manager dashboard or employee device.
          </p>
          <LoginForm onSuccess={onSuccess} />
          <p className="mt-6 text-sm text-slate-600 text-center">
            New here?{" "}
            <Link
              to="/signup"
              className="font-medium text-slate-900 underline"
              data-testid="login-signup-link"
            >
              Start a 7-day free trial
            </Link>
          </p>
          <DemoAccountList />
        </Card>
      </div>
    </div>
  );
}
